import { memo, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
} from 'constants/offer'
import { MINIMUM_RECOMMENDATION_OFFERS_TO_DISPLAY } from 'constants/recommendations'
import { trackClientEvent } from 'analytics/analytics'
import LayoutContainer from 'components/Common/LayoutContainer'
import { buildSearchStringFromFilters } from 'lib/search/searchUtils'

import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import useOfferList from 'hooks/Offers/useOfferList'
import { skip, take, without } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import type { OfferListEventsDispatchAction } from 'components/OfferList/OfferListEventsContext'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import type { OfferListAndAction } from 'components/OfferList/OfferList'
import OfferList, { OfferListAction } from 'components/OfferList/OfferList'
import getOfferListKey from 'lib/offer/offerListKey'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'

const emptyFilters: App.OfferListFilters = EmptyObject

interface Props {
  title?: string
  offerId?: string
  filters?: App.OfferListFilters
  offerDistances?: Record<string, number | undefined>
  offerDistanceFrom?: string
  trackingDescription: string
  propertyId?: string
  skipFirstNOffers?: number
  useList?: boolean
  overrideOfferList?: App.OfferList
  maxOffers?: number
  loadingOfferIds?: ReadonlySet<string>
  canJoinLereGraphAbTest?: boolean
  allLink?: string
  // use for overriding the prices and values when having checkIn/checkOut/Occupancy
  enableDateCheckingForPriceDisplay?: boolean
}

const DEFAULT_MAX_OFFERS = 8

export const RECOMMENDATION_TYPES: Array<App.OfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  'bedbank_hotel',
]

export function shouldUseOverrideList(overrideOfferList: App.OfferList | undefined, defaultListKey: string): overrideOfferList is App.OfferList {
  return !!overrideOfferList?.fetching ||
    !!overrideOfferList?.offerIds.length ||
    (!!overrideOfferList?.error && overrideOfferList.key === defaultListKey)
}

function OfferRecommendations(props: Props) {
  const intl = useIntl()
  const {
    title,
    offerId,
    filters = emptyFilters,
    offerDistances,
    offerDistanceFrom,
    trackingDescription,
    skipFirstNOffers,
    useList = false,
    overrideOfferList,
    loadingOfferIds,
    maxOffers = DEFAULT_MAX_OFFERS,
    allLink,
    enableDateCheckingForPriceDisplay = false,
  } = props

  const resolvedTitle = title ?? intl.formatMessage({
    id: 'LfkLEE7KBu5t1-yBAVIA',
    defaultMessage: 'You may also like',
    description: 'Default heading for offer recommendations section in the recommendations section',
  })

  const handleListEvent = useCallback((dispatchAction: OfferListEventsDispatchAction) => {
    if (dispatchAction.type === OfferListEvents.productClick && (filters.lere === 'graph' || filters.lere === 'lere-o2o' || filters.lere === 'twoTowers')) {
      trackClientEvent({
        subject: 'similar-offer-click',
        action: `ab-value: ${dispatchAction.position + 1}`,
        category: 'logging',
        type: 'operational',
        optimizelyEventId: '5827605312569344',
        optimizelyEventKey: 'similar_carousel_product_click',
      })
    }
  }, [filters])

  const { defaultOfferListFilters, defaultListKey } = useMemo(() => {
    const defaultOfferListFilters: App.OfferListFilters = {
      ...filters,
      offerTypes: RECOMMENDATION_TYPES,
    }

    return {
      defaultOfferListFilters,
      defaultListKey: getOfferListKey(defaultOfferListFilters),
    }
  }, [filters])
  const useOverrideList = shouldUseOverrideList(overrideOfferList, defaultListKey)
  const defaultOfferList = useOfferList(defaultOfferListFilters, { disabled: useOverrideList })

  const offerList: App.OfferList = useMemo(() => {
    if (useOverrideList) {
      return overrideOfferList
    }

    return defaultOfferList
  }, [defaultOfferList, overrideOfferList, useOverrideList])

  const selectedDatesAndRoomsFilter: App.OfferListFilters | undefined = useMemo(() => {
    return enableDateCheckingForPriceDisplay ? {
      checkIn: filters.checkIn,
      checkOut: filters.checkOut,
      rooms: filters.rooms,
    } : undefined
  }, [enableDateCheckingForPriceDisplay, filters])

  const offerIds = useMemo(() => {
    // offer list ids without source offer
    let offerCandidates = without(offerList.offerIds, offerId ?? '')
    // skip first few offers if specified
    if (skipFirstNOffers) {
      offerCandidates = skip(offerCandidates, skipFirstNOffers)
    }
    // take max offers
    offerCandidates = take(offerCandidates, maxOffers)
    return offerCandidates
  }, [offerId, offerList.offerIds, skipFirstNOffers, maxOffers])

  const algorithm = filters.lere === 'graph' ? 'Graph' :
    filters.lere === 'lere-o2o' ? 'Offer-to-offer' :
        (filters.propertyId ? 'Search Nearby' : 'Algolia')
  const tracking = useOfferListTracking(
    `Recommendations-${algorithm}-${trackingDescription}`, {
      ga: {
        category: 'Recommendations',
        label: `${algorithm}-${trackingDescription}`,
      },
      key: getOfferListKey(filters),
    },
  )

  const isIdsFetching = offerList.fetching

  const additionalQueryParams = useMemo(() => buildSearchStringFromFilters(filters), [filters])

  const toShowOfferList = useMemo<App.OfferList>(() => ({
    fetching: isIdsFetching,
    offerIds,
    key: 'offerRecommendations',
  }), [isIdsFetching, offerIds])

  const overrideOfferListAndAction = useMemo<OfferListAndAction>(() => ({
    action: OfferListAction.OVERRIDE,
    offerList: toShowOfferList,
  }), [toShowOfferList])
  const insufficientReco = offerIds.length < MINIMUM_RECOMMENDATION_OFFERS_TO_DISPLAY && !isIdsFetching
  if (insufficientReco) {
    return null
  }

  return (
    <OfferListEventsProvider tracking={tracking} onListEvent={handleListEvent}>
      <Sheet background="normal-background" padding="40 0" data-testid="offer-page-similar-carousel">
        <VerticalSpacer gap={16}>
          <LayoutContainer>
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={16}>
              <Heading variant="heading3">{resolvedTitle}</Heading>
              {!!allLink && <TextButton kind="tertiary" size="medium" to={allLink}>
                {intl.formatMessage({
                  id: 'iSKChpIjHw2Out5FzyUw',
                  defaultMessage: 'View all',
                  description: 'Button label to view all available offer recommendations',
                })}
              </TextButton>}
            </Group>
          </LayoutContainer>
          {!useList && <OfferListCarousel
            loadingOfferIds={loadingOfferIds}
            overrideOfferList={toShowOfferList}
            additionalOfferUrlQuery={additionalQueryParams}
            selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
            offerDistances={offerDistances}
            offerDistanceFrom={offerDistanceFrom}
          />}
          {useList && <OfferList
            extraOfferListAndAction={overrideOfferListAndAction}
            tileStyle="search"
          />}
        </VerticalSpacer>
      </Sheet>
    </OfferListEventsProvider>
  )
}

export default memo(OfferRecommendations)
